import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { AIBrainLoader } from '../components/ui/ai-loading';
import { toast } from 'sonner';
import {
  Mic,
  Square, FileText, Clock, User, Save,
  Volume2, Trash2,
} from 'lucide-react';
import { DashboardShell } from '../components/brand/DashboardShell';
import { clinicianNavItems } from '../components/brand/navItems';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VoiceNotes() {
  const { token } = useAuth();
  const location = useLocation();
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcription, setTranscription] = useState('');
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [noteType, setNoteType] = useState('visit');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/voice/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setNotes(await response.json());
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Recording started...');
    } catch (error) {
      toast.error('Could not access microphone');
      console.error('Recording error:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success('Recording stopped');
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob) {
      toast.error('No recording to transcribe');
      return;
    }

    setLoading(true);
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        const response = await fetch(`${API_URL}/api/voice/transcribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            audio_data: base64Audio,
            audio_format: 'webm',
            patient_id: selectedPatient || null,
            note_type: noteType
          })
        });

        if (response.ok) {
          const data = await response.json();
          setTranscription(data.transcription);
          toast.success('Transcription complete!');
          fetchNotes();
        } else {
          toast.error('Transcription failed');
        }
        setLoading(false);
      };
    } catch (error) {
      toast.error('Transcription error');
      setLoading(false);
    }
  };

  const clearRecording = () => {
    setAudioBlob(null);
    setTranscription('');
    audioChunksRef.current = [];
  };

  const noteTypes = [
    { value: 'visit', label: 'Visit Notes' },
    { value: 'follow_up', label: 'Follow-up Notes' },
    { value: 'assessment', label: 'Assessment' },
    { value: 'care_plan', label: 'Care Plan' }
  ];

  return (
    <DashboardShell
      navItems={clinicianNavItems({ pathname: location.pathname })}
      title="Voice Notes"
      subtitle="Record once. Whisper transcribes. You stay in control of the final note."
      testId="voice-notes"
    >
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Recording Section */}
          <div className="space-y-6">
            <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mic className="w-5 h-5 text-cn-primary" />
                  Voice-to-Text Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Note Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Note Type
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {noteTypes.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => setNoteType(type.value)}
                        className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                          noteType === type.value
                            ? 'bg-cn-sage-100 dark:bg-cn-sage-900/40 text-cn-primary-hover dark:text-cn-sage-300 border-2 border-cn-primary'
                            : 'bg-slate-100 dark:bg-cn-ink-700 text-slate-600 dark:text-slate-400 border-2 border-transparent'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Recording Button */}
                <div className="flex flex-col items-center py-8">
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={loading}
                    className={`w-24 h-24 rounded-full flex items-center justify-center transition-all transform hover:scale-105 ${
                      isRecording
                        ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                        : 'bg-gradient-to-br from-cn-primary to-cn-sage-500 hover:from-cn-primary hover:to-cn-sage-600'
                    } shadow-lg`}
                    data-testid="record-button"
                  >
                    {isRecording ? (
                      <Square className="w-10 h-10 text-white" />
                    ) : (
                      <Mic className="w-10 h-10 text-white" />
                    )}
                  </button>
                  <p className="text-slate-600 dark:text-slate-400 mt-4">
                    {isRecording ? 'Recording... Click to stop' : 'Click to start recording'}
                  </p>
                </div>

                {/* Audio Preview */}
                {audioBlob && (
                  <div className="p-4 bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Volume2 className="w-5 h-5 text-cn-primary" />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Recording Ready
                        </span>
                      </div>
                      <button
                        onClick={clearRecording}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <audio controls className="w-full" src={URL.createObjectURL(audioBlob)} />
                  </div>
                )}

                {/* Transcribe Button */}
                {audioBlob && !transcription && (
                  loading ? (
                    <div className="py-6">
                      <AIBrainLoader message="Transcribing audio with AI..." />
                      <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-2">
                        Converting speech to text using Whisper AI
                      </p>
                    </div>
                  ) : (
                    <Button
                      onClick={transcribeAudio}
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-cn-sage-500 to-green-600 hover:from-cn-sage-600 hover:to-green-700 text-white rounded-xl h-12"
                      data-testid="transcribe-button"
                    >
                      <FileText className="w-5 h-5 mr-2" />
                      Transcribe to Text
                    </Button>
                  )
                )}

                {/* Transcription Result */}
                {transcription && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                      Transcription
                    </label>
                    <Textarea
                      value={transcription}
                      onChange={(e) => setTranscription(e.target.value)}
                      rows={6}
                      className="resize-none"
                      data-testid="transcription-text"
                    />
                    <div className="flex gap-3">
                      <Button className="flex-1 bg-gradient-to-r from-cn-primary to-cn-sage-500 text-white rounded-xl">
                        <Save className="w-4 h-4 mr-2" />
                        Save Note
                      </Button>
                      <Button
                        variant="outline"
                        onClick={clearRecording}
                        className="rounded-xl"
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Notes History */}
          <div>
            <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-cn-primary" />
                  Recent Voice Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {notes.length === 0 ? (
                  <div className="text-center py-12">
                    <Mic className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-500 dark:text-slate-400">No voice notes yet</p>
                    <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                      Record your first voice note above
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {notes.map((note) => (
                      <div
                        key={note.id}
                        className="p-4 rounded-xl bg-slate-50 dark:bg-cn-ink-700/50 border border-slate-100 dark:border-cn-ink-650"
                        data-testid={`note-${note.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className="px-2 py-1 bg-cn-sage-100 dark:bg-cn-sage-900/40 text-cn-primary-hover dark:text-cn-sage-300 rounded-full text-xs font-medium capitalize">
                            {note.note_type}
                          </span>
                          <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                            <Clock className="w-3 h-3" />
                            {new Date(note.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-3">
                          {note.transcription}
                        </p>
                        {note.patient_id && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-slate-500 dark:text-slate-400">
                            <User className="w-3 h-3" />
                            Patient: {note.patient_id.slice(0, 8)}...
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
    </DashboardShell>
  );
}
